import React, { useEffect, useRef } from 'react';

const OtpSuccessPopup = ({ 
  show = false, 
  onClose,
  message = 'OTP verified successfully',
  title = 'OTP Verified',
  autoClose = true,
  autoCloseDelay = 2000
}) => {
  const timerRef = useRef(null);
  const onCloseRef = useRef(onClose);

  // Update onClose ref when it changes
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Debug log
  useEffect(() => {
    if (show) {
      console.log("🎉 OtpSuccessPopup: show = true, rendering popup");
    } else {
      console.log("🚫 OtpSuccessPopup: show = false, not rendering");
    }
  }, [show]);

  // Auto-close after delay if enabled
  useEffect(() => {
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (show && autoClose && onCloseRef.current) {
      console.log(`⏰ OtpSuccessPopup: Auto-close timer set for ${autoCloseDelay}ms`);
      timerRef.current = setTimeout(() => {
        console.log("⏰ OtpSuccessPopup: Auto-closing popup");
        if (onCloseRef.current) {
          onCloseRef.current();
        }
      }, autoCloseDelay);
      
      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      };
    }
  }, [show, autoClose, autoCloseDelay]);

  if (!show) {
    console.log("🚫 OtpSuccessPopup: Early return - show is false");
    return null;
  }

  console.log("✅ OtpSuccessPopup: Rendering popup component");

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001] p-4"
      style={{ zIndex: 10001 }}
      onClick={(e) => {
        if (e.target === e.currentTarget && onClose) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 md:p-8 animate-slideUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Success Icon with Animation */}
        <div className="mb-6 flex justify-center">
          <div className="relative">
            {/* Outer ring animation */}
            <div className="absolute inset-0 bg-green-100 rounded-full animate-ping opacity-75"></div>
            {/* Main icon circle */}
            <div className="relative w-20 h-20 md:w-24 md:h-24 bg-green-100 rounded-full flex items-center justify-center">
              <svg 
                className="w-10 h-10 md:w-12 md:h-12 text-green-600" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={3} 
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" 
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Title */}
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-3">
          {title}
        </h2>

        {/* Message */}
        <p className="text-gray-600 text-center text-base md:text-lg mb-6 whitespace-pre-line">
          {message}
        </p>

        {/* Additional Info */}
        <div className="bg-green-50 rounded-lg p-4 mb-6 border border-green-200">
          <div className="flex items-start space-x-3">
            <svg 
              className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
            <p className="text-green-800 text-sm font-medium">
              Your ride has started. The driver will now proceed to your destination.
            </p>
          </div>
        </div>

        {/* Close Button */}
        {onClose && (
          <div className="flex justify-center">
            <button
              onClick={onClose}
              className="w-full md:w-auto px-8 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-all duration-200 transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl"
            >
              OK
            </button>
          </div>
        )}

        {/* Auto-close indicator (if enabled) */}
        {autoClose && (
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500">
              This message will close automatically
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OtpSuccessPopup;

